import type { IEventBus, EventCallback, UnsubscribeFn } from './types';

/**
 * Event Bus implementation for WESTEER.
 * Provides publish-subscribe pattern for decoupled module communication.
 *
 * @example
 * const eventBus = new EventBus();
 *
 * // Subscribe to events
 * const unsubscribe = eventBus.on('mobility.stop.created', (payload) => {
 *   console.log('New stop:', payload.stop);
 * });
 *
 * // Emit events
 * eventBus.emit('mobility.stop.created', { stop: { id: '1', name: 'Main St' } });
 *
 * // Cleanup
 * unsubscribe();
 */
export class EventBus implements IEventBus {
  private listeners: Map<string, EventCallback[]> = new Map();

  /**
   * Escucha un evento.
   * @param event - Nombre del evento (convención: 'modulo.entidad.accion')
   * @param callback - Función a ejecutar cuando ocurra el evento
   * @returns Función para desuscribirse
   */
  public on<T = any>(event: string, callback: EventCallback<T>): UnsubscribeFn {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }

    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.push(callback as EventCallback);
    }

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Emite un evento.
   * Los listeners se ejecutan sincrónicamente en el orden en que fueron registrados.
   * Si un listener lanza un error, se captura y se registra, pero no afecta a otros listeners.
   * @param event - Nombre del evento
   * @param payload - Datos del evento (debe ser serializable)
   */
  public emit<T = any>(event: string, payload: T): void {
    const callbacks = this.listeners.get(event) || [];

    callbacks.forEach(callback => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        callback(payload);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`[EventBus] Error in listener for '${event}':`, error);
        // NO romper si un listener falla - continuar con otros listeners
      }
    });
  }

  /**
   * Remueve un listener de un evento.
   * @param event - Nombre del evento
   * @param callback - Función a remover
   */
  public off<T = any>(event: string, callback: EventCallback<T>): void {
    const callbacks = this.listeners.get(event);
    if (!callbacks) {
      return;
    }

    const index = callbacks.indexOf(callback as EventCallback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }

    // Cleanup: remove event entry if no more listeners
    if (callbacks.length === 0) {
      this.listeners.delete(event);
    }
  }

  /**
   * Escucha un evento una sola vez.
   * El listener se desuscribe automáticamente después de la primera ejecución.
   * @param event - Nombre del evento
   * @param callback - Función a ejecutar
   * @returns Función para desuscribirse (si se necesita antes de la primera ejecución)
   */
  public once<T = any>(event: string, callback: EventCallback<T>): UnsubscribeFn {
    const wrapper: EventCallback<T> = (payload: T) => {
      void callback(payload);
      this.off(event, wrapper);
    };

    return this.on(event, wrapper);
  }

  /**
   * Obtiene el número de listeners registrados para un evento.
   * Útil para debugging y testing.
   * @param event - Nombre del evento
   * @returns Número de listeners registrados
   */
  public listenerCount(event: string): number {
    const callbacks = this.listeners.get(event);
    return callbacks ? callbacks.length : 0;
  }

  /**
   * Obtiene todos los eventos que tienen listeners registrados.
   * Útil para debugging.
   * @returns Array de nombres de eventos
   */
  public getEvents(): string[] {
    return Array.from(this.listeners.keys());
  }

  /**
   * Remueve todos los listeners de todos los eventos.
   * Útil para cleanup en tests.
   */
  public clear(): void {
    this.listeners.clear();
  }
}

/**
 * Singleton instance del Event Bus.
 * Exportado para uso en toda la aplicación.
 */
export const eventBus = new EventBus();
